/**
 * The approval queue — one for every kind of document.
 *
 * A list first, so a person sees at a glance what is waiting, for how much,
 * since when and with whom; the request itself opens beside it, with what it
 * says, its route, its history and the decision. Each request still shows
 * why somebody cannot decide it — the person who prepared it sees the reason
 * rather than an Approve button that refuses. Offering the button and then
 * refusing the click teaches people the control is arbitrary; showing why
 * teaches them it is deliberate.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, DOC_KIND_LABELS, formatInr, type QueueDecision, type QueueItem } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Loading, Note, Pill, Stat } from '../components/ui'

type Outcome = 'approve' | 'reject' | 'return'
type View = 'mine' | 'others' | 'decided'
type Sort = 'oldest' | 'newest' | 'largest'

type HistoryItem = QueueItem & { my_decision: QueueDecision; request_status: string }

const DECISION_WORDS: Record<string, string> = {
  approve: 'Approved',
  approved: 'Approved',
  reject: 'Rejected',
  rejected: 'Rejected',
  return: 'Sent back',
  returned: 'Sent back',
  self_released: 'Self-released',
}

const STATUS_WORDS: Record<string, string> = {
  submitted: 'still in approval',
  approved: 'fully approved',
  rejected: 'rejected',
  returned: 'sent back for correction',
  withdrawn: 'withdrawn',
  recalled_stale: 'edited after approval, resubmitted',
  draft: 'draft',
}

function kindLabel(kind: string) {
  return DOC_KIND_LABELS[kind] ?? kind.replace(/_/g, ' ')
}

function when(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function day(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
  })
}

//: How long a request has been waiting, in words a person reads at a glance.
function age(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day'
  return `${days} days`
}

function currentStep(item: QueueItem) {
  return item.steps.find((s) => s.current)
}

//: The one word that says where a request stands for the viewer.
function standing(item: QueueItem): { label: string; tone: string } {
  if (item.can_decide) return { label: 'Your turn', tone: 'turn' }
  if (item.overdue) return { label: 'Overdue', tone: 'blocking' }
  if (item.i_prepared_this) return { label: 'You prepared', tone: 'closed' }
  const step = currentStep(item)
  return { label: `With ${step?.assigned_name ?? step?.label ?? 'an approver'}`, tone: 'advisory' }
}

function Progress({ item }: { item: QueueItem }) {
  const step = currentStep(item)
  const done = item.steps.filter((s) => s.satisfied).length
  return (
    <div className="appr-progress" title={item.steps.map((s) => s.label).join(' → ')}>
      <span className="dots" aria-hidden="true">
        {item.steps.map((s) => (
          <i key={s.step_no} className={s.satisfied ? 'done' : s.current ? 'now' : ''} />
        ))}
      </span>
      <span className="muted">
        {step
          ? `Step ${step.step_no} of ${item.steps.length} · ${step.label}`
          : `${done} of ${item.steps.length} done`}
      </span>
    </div>
  )
}

function Route({ item }: { item: QueueItem }) {
  return (
    <ol className="appr-route">
      {item.steps.map((s) => (
        <li key={s.step_no} className={s.satisfied ? 'done' : s.current ? 'now' : 'later'}>
          <div className="route-head">
            <strong>{s.label}</strong>
            {s.satisfied ? (
              <span className="pill pass">approved</span>
            ) : s.current ? (
              <span className="pill advisory">waiting</span>
            ) : (
              <span className="pill closed">later</span>
            )}
          </div>
          <div className="faint">
            {s.assigned_name ??
              (s.kind === 'department_authority'
                ? "the department's approver"
                : 'any approver with the authority')}
            {s.required > 1 && ` · ${s.approvals} of ${s.required} approvals`}
            {s.stale_approvals > 0 && ` · ${s.stale_approvals} lapsed after an edit`}
          </div>
          {s.note && <div className="faint">{s.note}</div>}
          {s.rerouted_from_name && (
            <div className="faint">
              moved from {s.rerouted_from_name}: {s.reroute_reason}
            </div>
          )}
        </li>
      ))}
    </ol>
  )
}

function Drawer({
  item,
  onClose,
  onDecided,
}: {
  item: QueueItem
  onClose: () => void
  onDecided: () => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const step = currentStep(item)
  const stand = standing(item)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const decide = useMutation({
    mutationFn: (vars: { outcome: Outcome; breakGlass?: boolean }) =>
      api.post<QueueItem>(`/approvals/requests/${item.request_id}/decide`, {
        outcome: vars.outcome,
        reason: reason.trim() || null,
        break_glass: vars.breakGlass ?? false,
        step_no: step?.step_no ?? null,
      }),
    onSuccess: () => {
      setReason('')
      // Every list a decision can move.
      void queryClient.invalidateQueries()
      onDecided()
    },
  })

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${kindLabel(item.doc_kind)} ${item.reference}`}
      >
        <header className="drawer-head">
          <div>
            <div className="appr-kind">{kindLabel(item.doc_kind)}</div>
            <h2>{item.reference}</h2>
            <div className="muted">
              Prepared by {item.maker}
              {item.department && ` · ${item.department}`} · submitted {when(item.submitted_at)}
            </div>
          </div>
          <div className="drawer-amount">
            <div className="value">{formatInr(item.amount_paise)}</div>
            <span className={`pill ${stand.tone}`}>{stand.label}</span>
            {item.sla_due_at && (
              <div className={item.overdue ? 'due overdue' : 'due'}>
                {item.overdue ? 'was due' : 'due'} {day(item.sla_due_at)}
              </div>
            )}
          </div>
          <button className="quiet close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="drawer-body">
          <ErrorNote error={decide.error} />
          <section>
            <h3>What is being decided</h3>
            <p className="appr-summary">{item.summary}</p>
            {/* Without the facts an approver agrees to a reference and an
                amount: a signature on something they have not read. */}
            {item.facts.length > 0 && (
              <dl className="appr-facts">
                {item.facts.map((f) => (
                  <div key={f.label}>
                    <dt>{f.label}</dt>
                    <dd>{f.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="faint" style={{ marginBottom: 0 }}>
              {item.state}
            </p>
          </section>

          <section>
            <h3>Approval route</h3>
            <Route item={item} />
          </section>

          {item.decisions.length > 0 && (
            <section>
              <h3>History</h3>
              <ul className="trail">
                {item.decisions.map((d, i) => (
                  <li key={i}>
                    <strong>{DECISION_WORDS[d.decision] ?? d.decision.replace(/_/g, ' ')}</strong>{' '}
                    by {d.decided_by}
                    {d.on_behalf_of && ` for ${d.on_behalf_of}`}{' '}
                    <span className="faint">
                      · step {d.step_no} · {when(d.at)}
                    </span>
                    {d.reason && <div className="said">“{d.reason}”</div>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <footer className="drawer-foot">
          {item.can_decide ? (
            <>
              <label className="appr-note">
                <span className="caption">
                  Your decision on “{step?.label ?? 'this step'}”
                  <span className="faint"> — a note is required to reject or send back</span>
                </span>
                <textarea
                  rows={2}
                  value={reason}
                  placeholder="Note (kept in the audit trail)"
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <div className="actions" style={{ marginTop: 8 }}>
                <button
                  className="primary"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ outcome: 'approve' })}
                >
                  {decide.isPending ? 'Saving…' : 'Approve'}
                </button>
                <button
                  disabled={decide.isPending || !reason.trim()}
                  onClick={() => decide.mutate({ outcome: 'return' })}
                  title={reason.trim() ? undefined : 'Say what needs correcting first'}
                >
                  Send back for correction
                </button>
                <button
                  className="danger"
                  disabled={decide.isPending || !reason.trim()}
                  onClick={() => decide.mutate({ outcome: 'reject' })}
                  title={reason.trim() ? undefined : 'Say why before rejecting'}
                >
                  Reject
                </button>
              </div>
            </>
          ) : (
            <>
              <Note kind={item.i_prepared_this ? 'warn' : 'info'}>{item.blocked_reason}</Note>
              {item.break_glass_available && (
                <div className="inset">
                  <strong>Release without a checker</strong>
                  <p className="muted" style={{ margin: '4px 0 8px' }}>
                    Nobody else can decide this step, so you may release it yourself. It is recorded
                    as a <em>self-release</em>, never as an approval, and raises an item somebody
                    has to clear. Amounts above {formatInr(item.break_glass_ceiling_paise)} wait
                    regardless.
                  </p>
                  <textarea
                    rows={2}
                    value={reason}
                    placeholder="Why this cannot wait (required, kept forever)"
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="actions">
                    <button
                      className="danger"
                      disabled={decide.isPending || !reason.trim()}
                      onClick={() => decide.mutate({ outcome: 'approve', breakGlass: true })}
                    >
                      Self-release
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </footer>
      </aside>
    </>
  )
}

function DecidedByYou({ search }: { search: string }) {
  const history = useQuery({
    queryKey: ['approvals', 'history'],
    queryFn: () => api.get<HistoryItem[]>('/approvals/history'),
    staleTime: 0,
  })
  const q = search.trim().toLowerCase()
  const rows = (history.data ?? []).filter(
    (r) =>
      !q ||
      [r.reference, r.summary, r.maker, r.department ?? '', kindLabel(r.doc_kind)]
        .join(' ')
        .toLowerCase()
        .includes(q),
  )
  if (history.isLoading) return <Loading what="Loading your decisions" />
  return (
    <>
      <ErrorNote error={history.error} />
      {rows.length === 0 ? (
        <div className="empty">
          Nothing yet. Requests you approve, reject or send back are listed here.
        </div>
      ) : (
        <table className="appr-table">
          <thead>
            <tr>
              <th>Request</th>
              <th className="num">Amount</th>
              <th>Prepared by</th>
              <th>Your decision</th>
              <th>Where it is now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const d = item.my_decision
              const step = item.steps.find((s) => s.step_no === d.step_no)
              return (
                <tr key={item.request_id}>
                  <td>
                    <div className="appr-kind">{kindLabel(item.doc_kind)}</div>
                    <strong>{item.reference}</strong>
                    <div className="faint clipped" title={item.summary}>
                      {item.summary}
                    </div>
                  </td>
                  <td className="num">
                    <strong>{formatInr(item.amount_paise)}</strong>
                  </td>
                  <td>
                    {item.maker}
                    {item.department && <div className="faint">{item.department}</div>}
                  </td>
                  <td>
                    {DECISION_WORDS[d.decision] ?? d.decision}
                    {step && <span className="muted"> · {step.label}</span>}
                    <div className="faint">
                      {when(d.at)}
                      {d.on_behalf_of && ` · for ${d.on_behalf_of}`}
                    </div>
                    {d.reason && <div className="faint">“{d.reason}”</div>}
                  </td>
                  <td>
                    <Pill value={item.request_status} />
                    <div className="faint">
                      {STATUS_WORDS[item.request_status] ?? item.request_status}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </>
  )
}

export default function Approvals() {
  const { can } = useAuth()
  const [view, setView] = useState<View>('mine')
  const [everything, setEverything] = useState(false)
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('')
  const [sort, setSort] = useState<Sort>('oldest')
  const [openId, setOpenId] = useState<string | null>(null)
  const readsEverything = can('audit.log.view')

  const queue = useQuery({
    queryKey: ['approvals', 'queue', everything],
    queryFn: () => api.get<QueueItem[]>(`/approvals/queue?everything=${everything}`),
    staleTime: 0,
  })

  const all = useMemo(() => queue.data ?? [], [queue.data])
  const mine = all.filter((r) => r.can_decide)
  const others = all.filter((r) => !r.can_decide)
  const overdue = all.filter((r) => r.overdue)
  const oldest = mine.length
    ? mine.reduce((a, b) => (a.submitted_at < b.submitted_at ? a : b))
    : null
  const kinds = Array.from(new Set(all.map((r) => r.doc_kind))).sort()

  const q = search.trim().toLowerCase()
  const shown = (view === 'mine' ? mine : others)
    .filter((r) => !kind || r.doc_kind === kind)
    .filter(
      (r) =>
        !q ||
        [r.reference, r.summary, r.maker, r.department ?? '', kindLabel(r.doc_kind)]
          .join(' ')
          .toLowerCase()
          .includes(q),
    )
    .sort((a, b) =>
      sort === 'largest'
        ? b.amount_paise - a.amount_paise
        : sort === 'newest'
          ? b.submitted_at.localeCompare(a.submitted_at)
          : a.submitted_at.localeCompare(b.submitted_at),
    )
  const open = all.find((r) => r.request_id === openId) ?? null

  if (queue.isLoading) return <Loading what="Loading the approval queue" />

  return (
    <>
      <ErrorNote error={queue.error} />

      <div className="grid">
        <Stat
          label="Waiting on you"
          value={String(mine.length)}
          note={
            mine.length
              ? formatInr(mine.reduce((t, r) => t + r.amount_paise, 0))
              : 'nothing to decide'
          }
        />
        <Stat
          label="Overdue"
          value={String(overdue.length)}
          note={overdue.length ? 'past the time the rule allows' : 'everything is on time'}
        />
        <Stat
          label="With others"
          value={String(others.length)}
          note="in approval, waiting on someone else"
        />
        <Stat
          label="Oldest waiting on you"
          value={oldest ? age(oldest.submitted_at) : '—'}
          note={oldest ? `${kindLabel(oldest.doc_kind)} ${oldest.reference}` : 'nothing waiting'}
        />
      </div>

      <section className="card">
        <header className="appr-toolbar">
          <div className="seg" role="tablist">
            {(
              [
                ['mine', `Waiting on you`, mine.length],
                ['others', 'With others', others.length],
                ['decided', 'Decided by you', null],
              ] as [View, string, number | null][]
            ).map(([key, label, n]) => (
              <button
                key={key}
                role="tab"
                aria-selected={view === key}
                className={view === key ? 'on' : undefined}
                onClick={() => setView(key)}
              >
                {label}
                {n !== null && <span className="count">{n}</span>}
              </button>
            ))}
          </div>
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Search reference, person, summary…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search requests"
            />
            {view !== 'decided' && (
              <>
                <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Type">
                  <option value="">All types</option>
                  {kinds.map((k) => (
                    <option key={k} value={k}>
                      {kindLabel(k)}
                    </option>
                  ))}
                </select>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as Sort)}
                  aria-label="Sort"
                >
                  <option value="oldest">Oldest first</option>
                  <option value="newest">Newest first</option>
                  <option value="largest">Largest amount</option>
                </select>
              </>
            )}
            {readsEverything && view === 'others' && (
              <label className="appr-check">
                <input
                  type="checkbox"
                  checked={everything}
                  onChange={(e) => setEverything(e.target.checked)}
                />
                Everything pending
              </label>
            )}
          </div>
        </header>
        <div className="body tight">
          {view === 'decided' ? (
            <DecidedByYou search={search} />
          ) : shown.length === 0 ? (
            <div className="empty">
              {view === 'mine'
                ? q || kind
                  ? 'Nothing waiting on you matches.'
                  : 'Nothing is waiting on you. 🎉'
                : 'Nothing is with anybody else.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th className="num">Amount</th>
                  <th>Prepared by</th>
                  <th>Waiting</th>
                  <th>Stage</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => {
                  const stand = standing(item)
                  return (
                    <tr
                      key={item.request_id}
                      className={`appr-row${openId === item.request_id ? ' selected' : ''}`}
                      tabIndex={0}
                      onClick={() => setOpenId(item.request_id)}
                      onKeyDown={(e) => e.key === 'Enter' && setOpenId(item.request_id)}
                    >
                      <td>
                        <div className="appr-kind">{kindLabel(item.doc_kind)}</div>
                        <strong>{item.reference}</strong>
                        <div className="faint clipped" title={item.summary}>
                          {item.summary}
                        </div>
                      </td>
                      <td className="num">
                        <strong>{formatInr(item.amount_paise)}</strong>
                      </td>
                      <td>
                        {item.maker}
                        {item.department && <div className="faint">{item.department}</div>}
                      </td>
                      <td>
                        {age(item.submitted_at)}
                        <div className="faint">since {day(item.submitted_at)}</div>
                      </td>
                      <td>
                        <Progress item={item} />
                      </td>
                      <td>
                        <span className={`pill ${stand.tone}`}>{stand.label}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <details className="appr-how">
        <summary>How approvals work</summary>
        <p className="muted">
          A request goes through its route in order: the department's approval authority confirms
          the need, then the Finance Head approves the money. Nobody decides two steps of the same
          request, and nobody approves what they prepared. An approval is bound to the exact content
          it was given on: edit the amount, the accounts or the date and every approval lapses, and
          the route starts again. Sending something back is not a rejection: the person who prepared
          it corrects it and submits again. Nothing here auto-approves.
          {can('org.approval_rule.view') && (
            <>
              {' '}
              The routes are set under <Link to="/setup/approval-rules">Approval rules</Link>.
            </>
          )}
        </p>
      </details>

      {open && (
        <Drawer item={open} onClose={() => setOpenId(null)} onDecided={() => setOpenId(null)} />
      )}
    </>
  )
}
